import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { API } from "@/lib/admin-constants";
import { Gift, Users, Trophy, Copy, Loader2, ExternalLink } from "lucide-react";

const ReferralManager = () => {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState({ is_active: true, reward_description: "", reward_amount: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [adminRes, settingsRes] = await Promise.all([
        axios.get(`${API}/referrals/admin`, { withCredentials: true }),
        axios.get(`${API}/referrals/settings`),
      ]);
      setData(adminRes.data);
      setSettings(settingsRes.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/referrals/settings`, settings, { withCredentials: true });
      toast.success("Settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-cfc-primary" size={32} /></div>;

  return (
    <div data-testid="referral-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-cfc-primary">Referral Program</h1>
          <p className="text-sm text-cfc-text-muted mt-1">{data?.total_codes || 0} referrers &middot; {data?.total_referrals || 0} total referrals</p>
        </div>
        <a href="/referral" target="_blank" rel="noopener noreferrer">
          <Button variant="outline" className="border-cfc-primary text-cfc-primary" data-testid="view-public-referral-btn">
            <ExternalLink size={16} className="mr-1.5" /> View Public Page
          </Button>
        </a>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <Card className="border-cfc-border">
          <CardContent className="p-4 text-center">
            <Gift size={24} className="mx-auto text-[#C8E64E] mb-1" />
            <p className="text-2xl font-bold text-cfc-primary">{data?.total_codes || 0}</p>
            <p className="text-xs text-cfc-text-muted font-bold uppercase">Referrers</p>
          </CardContent>
        </Card>
        <Card className="border-cfc-border">
          <CardContent className="p-4 text-center">
            <Users size={24} className="mx-auto text-[#C8E64E] mb-1" />
            <p className="text-2xl font-bold text-cfc-primary">{data?.total_referrals || 0}</p>
            <p className="text-xs text-cfc-text-muted font-bold uppercase">Referrals</p>
          </CardContent>
        </Card>
        <Card className="border-cfc-border">
          <CardContent className="p-4 text-center">
            <Trophy size={24} className="mx-auto text-[#C8E64E] mb-1" />
            <p className="text-2xl font-bold text-cfc-primary">{data?.codes?.length > 0 ? data.codes[0].referral_count : 0}</p>
            <p className="text-xs text-cfc-text-muted font-bold uppercase">Top Referrer</p>
          </CardContent>
        </Card>
      </div>

      {/* Settings */}
      <div className="bg-white border border-cfc-border rounded-sm p-5 mb-8" data-testid="referral-settings">
        <h2 className="font-heading text-lg font-bold text-cfc-primary mb-4">Program Settings</h2>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={settings.is_active} onChange={(e) => setSettings((p) => ({ ...p, is_active: e.target.checked }))} className="accent-cfc-primary" />
            <label className="text-sm text-cfc-primary font-medium">Program Active</label>
          </div>
          <div>
            <label className="text-xs font-bold text-cfc-text-muted uppercase">Reward Description</label>
            <Input value={settings.reward_description || ""} onChange={(e) => setSettings((p) => ({ ...p, reward_description: e.target.value }))} placeholder="e.g. Earn credit for every family you refer!" className="mt-1" data-testid="reward-desc" />
          </div>
          <div>
            <label className="text-xs font-bold text-cfc-text-muted uppercase">Reward Amount</label>
            <Input value={settings.reward_amount || ""} onChange={(e) => setSettings((p) => ({ ...p, reward_amount: e.target.value }))} placeholder="e.g. $25 credit per referral" className="mt-1" data-testid="reward-amount" />
          </div>
          <Button className="bg-cfc-primary text-white" onClick={handleSaveSettings} disabled={saving} data-testid="save-referral-settings-btn">
            {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null} Save Settings
          </Button>
        </div>
      </div>

      <Separator className="my-6" />

      {/* Referrer List */}
      <h2 className="font-heading text-lg font-bold text-cfc-primary mb-4">All Referrers</h2>
      {(data?.codes || []).length === 0 ? (
        <p className="text-sm text-cfc-text-muted">No referrers yet. Share the referral page link to get started.</p>
      ) : (
        <div className="space-y-2">
          {(data?.codes || []).map((ref) => (
            <div key={ref.referral_id} className="bg-white border border-cfc-border rounded-sm p-3" data-testid={`referrer-row-${ref.referral_id}`}>
              <div className="flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-sm text-cfc-primary">{ref.name}</h3>
                    <span className="text-xs bg-cfc-cream-alt text-cfc-text-muted px-1.5 py-0.5 rounded font-mono">{ref.code}</span>
                  </div>
                  <p className="text-[11px] text-cfc-text-muted">{ref.email}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-cfc-primary">{ref.referral_count}</p>
                  <p className="text-[10px] text-cfc-text-muted uppercase font-bold">Referrals</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(`https://commonwealthfc.org/referral?ref=${ref.code}`); toast.success("Link copied"); }} data-testid={`copy-ref-${ref.code}`}>
                  <Copy size={14} />
                </Button>
              </div>
              {ref.referred_leads?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-cfc-border">
                  <p className="text-[10px] text-cfc-text-muted uppercase font-bold mb-1">Referred:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {ref.referred_leads.map((r, i) => (
                      <span key={i} className="text-[10px] bg-green-50 text-green-700 px-1.5 py-0.5 rounded border border-green-200">
                        {r.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReferralManager;
